import React, { useState } from "react";
import { backendApi } from "./api/backendApi";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Download, DollarSign, BarChart3, PieChart as PieChartIcon } from "lucide-react";
import { toast } from "sonner";
import DetailModal from "./DetailModal";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// ---------------------------------------------------------------------
//  THEME TOKENS — matches the flat editorial surface used across HRMS
//  (kept in sync with the tokens in EmployeeDashboard.jsx)
// ---------------------------------------------------------------------
const T = {
  green: '#1A3C2B',
  greenDeep: '#122B1F',
  greenTint: '#EDF3EF',
  bg: '#F6F6F3',
  card: '#FFFFFF',
  line: '#E7E6E0',
  lineSoft: '#F0EFEA',
  ink: '#15181C',
  body: '#3F4650',
  muted: '#6B7280',
  faint: '#9CA3AF',
};

const DEPT_COLORS = ['#1A3C2B', '#3B6FA6', '#E0A33E', '#7E6BE0', '#D9534F', '#2F7D4F'];

// ---------------------------------------------------------------------
//  PRIMITIVES (mirrors the primitives used on the Dashboard page)
// ---------------------------------------------------------------------
const Panel = ({ title, icon: Icon, children }) => (
  <section className="hrms-card flex flex-col">
    {title && (
      <div className="flex items-center gap-2 px-5 pt-4 pb-3">
        {Icon && <Icon className="w-[15px] h-[15px] flex-shrink-0" style={{ color: T.green }} strokeWidth={2} />}
        <h3 className="text-[15px] font-bold" style={{ color: T.ink }}>{title}</h3>
      </div>
    )}
    <div className="px-5 pb-5 flex-1 min-w-0">{children}</div>
  </section>
);

const Tag = ({ label, tone }) => (
  <span className="inline-block mt-2 px-2.5 py-1 rounded-md text-[11px] font-semibold"
    style={{ background: tone.bg, color: tone.fg }}>
    {label}
  </span>
);

const MetricTile = ({ value, label, tagLabel, tone }) => (
  <div className="hrms-card p-5">
    <p className="text-[22px] font-extrabold leading-tight truncate" style={{ color: T.ink }}>{value}</p>
    <p className="text-[12.5px] mt-1" style={{ color: T.muted }}>{label}</p>
    <Tag label={tagLabel} tone={tone} />
  </div>
);

const StatusPill = ({ status }) => {
  const s = String(status || '').toLowerCase();
  const tone =
    s.includes('paid') || s.includes('process')
      ? { bg: '#E8F3EC', fg: '#2F7D4F' }
      : s.includes('pending')
        ? { bg: '#FBF1E1', fg: '#B9822E' }
        : { bg: '#F3F2ED', fg: '#6B7280' };
  return (
    <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold whitespace-nowrap"
      style={{ background: tone.bg, color: tone.fg }}>
      {status}
    </span>
  );
};

const fmtAxis = (value) => {
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(0)}k`;
  return `₹${value}`;
};

export default function Payroll() {
  const queryClient = useQueryClient();
  const [viewing, setViewing] = useState(null);
  const [hoverMonth, setHoverMonth] = useState(null);
  const [hoverDept, setHoverDept] = useState(null);

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => backendApi.entities.Employee.list(),
    initialData: [],
  });

  // Persisted payroll runs from the backend Payroll entity.
  const { data: payrollRecords = [] } = useQuery({
    queryKey: ['payroll'],
    queryFn: () => backendApi.entities.Payroll.list(),
    initialData: [],
  });

  const now = new Date();
  const currentMonth = MONTHS[now.getMonth()];
  const currentYear = now.getFullYear();

  // Monthly net-payout totals, aggregated on the server.
  const { data: monthlyData = [] } = useQuery({
    queryKey: ['payroll-monthly'],
    queryFn: () => backendApi.payroll.monthlySummary(),
    initialData: [],
  });

  // Payroll is computed and persisted by the backend (gross, statutory
  // deductions, net), the client only triggers the run.
  const runPayrollMutation = useMutation({
    mutationFn: () => backendApi.payroll.run(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll-monthly'] });
      queryClient.invalidateQueries({ queryKey: ['payroll'] });
      toast.success(`Payroll processed for ${currentMonth} ${currentYear}`);
    },
    onError: (err) => toast.error(err.message || "Failed to run payroll"),
  });

  const handleDownloadPayslips = () => {
    if (payrollRecords.length === 0) {
      toast.error("No payroll records to download. Run payroll first.");
      return;
    }
    const headers = ["Employee ID", "Name", "Department", "Position", "Month", "Year", "Gross", "Deductions", "Net", "Status"];
    const rows = payrollRecords.map((p) => [
      p.employee_id, p.employee_name, p.department, p.position, p.month, p.year,
      p.gross_salary, p.deductions, p.net_salary, p.status,
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `payslips_${currentMonth}_${currentYear}.csv`;
    a.click();
    toast.success("Payslips exported");
  };

  // Prefer real payroll rows; fall back to deriving from employees when none exist yet.
  const hasPayroll = payrollRecords.length > 0;

  const totalPayroll = hasPayroll
    ? payrollRecords.reduce((sum, p) => sum + (Number(p.net_salary) || 0), 0)
    : employees.reduce((sum, emp) => sum + (Number(emp.salary) || 0), 0);
  const employeesPaid = hasPayroll
    ? payrollRecords.filter((p) => p.status === "Processed" || p.status === "Paid").length
    : employees.filter(e => e.status === "Active").length;
  const totalDeductions = payrollRecords.reduce((sum, p) => sum + (Number(p.deductions) || 0), 0);
  const pendingSalaries = payrollRecords.filter((p) => p.status === "Pending").length;

  const deptPayroll = employees.reduce((acc, emp) => {
    if (!acc[emp.department]) acc[emp.department] = 0;
    acc[emp.department] += Number(emp.salary) || 0;
    return acc;
  }, {});

  const deptData = Object.keys(deptPayroll).length > 0
    ? Object.keys(deptPayroll).map(dept => ({ name: dept, value: deptPayroll[dept] }))
    : [{ name: 'No Data', value: 1 }];

  // ---- monthly bar chart scale --------------------------------------------
  // Picks a "nice" round step (1/2/5 × a power of ten) instead of blindly
  // dividing the raw peak by 4 — avoids axes like ₹0/₹1/₹2/₹3/₹4 when the
  // real numbers are zero or tiny.
  const niceStep = (raw) => {
    if (raw <= 0) return 1000;
    const exp = Math.floor(Math.log10(raw));
    const base = Math.pow(10, exp);
    const frac = raw / base;
    const niceFrac = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
    return niceFrac * base;
  };
  const monthPeakRaw = Math.max(0, ...monthlyData.map(m => m.amount || 0));
  const monthStep = monthPeakRaw > 0 ? niceStep(monthPeakRaw / 4) : 20000;
  const monthTop = monthStep * 4;
  const monthTicks = [monthTop, monthStep * 3, monthStep * 2, monthStep, 0];

  // ---- department donut ---------------------------------------------------
  const deptRawTotal = deptData.reduce((sum, d) => sum + d.value, 0);
  const deptTotal = deptRawTotal || 1; // safe denominator for the arc math below, never shown directly
  const CIRC = 2 * Math.PI * 42;
  let deptAcc = 0;

  return (
    <div className="p-6 md:p-8" style={{ background: T.bg, minHeight: '100%' }}>
      {/* Header row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-[19px] font-bold tracking-tight" style={{ color: T.ink }}>Payroll Dashboard</h2>
          <p className="text-[12px] mt-0.5" style={{ color: T.muted }}>Manage salary and compensation</p>
        </div>
        <div className="flex gap-2.5 flex-wrap">
          <button onClick={handleDownloadPayslips} className="hrms-btn-ghost inline-flex items-center gap-2">
            <Download className="w-3.5 h-3.5" strokeWidth={2} />
            Download Payslips
          </button>
          <button
            onClick={() => runPayrollMutation.mutate()}
            disabled={runPayrollMutation.isLoading}
            className="hrms-btn-primary inline-flex items-center gap-2"
          >
            <DollarSign className="w-3.5 h-3.5" strokeWidth={2} />
            {runPayrollMutation.isLoading ? "Processing…" : `Run Payroll · ${currentMonth}`}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricTile value={`₹${totalPayroll.toLocaleString()}`} label="Total Payroll" tagLabel="+5.2%" tone={{ bg: '#E8F3EC', fg: '#2F7D4F' }} />
        <MetricTile value={employeesPaid} label="Employees Paid" tagLabel="+3" tone={{ bg: '#EAF1FB', fg: '#3B6FA6' }} />
        <MetricTile value={pendingSalaries} label="Pending Salaries" tagLabel="This month" tone={{ bg: '#FBF1E1', fg: '#B9822E' }} />
        <MetricTile value={`₹${totalDeductions.toLocaleString()}`} label="Total Deductions" tagLabel="Processed" tone={{ bg: '#EFEAFA', fg: '#7E6BE0' }} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <Panel title="Monthly Salary Payout" icon={BarChart3}>
          <div className="flex gap-3 h-[220px]">
            <div className="flex flex-col justify-between py-1 text-[10px] font-medium text-right w-10 flex-shrink-0"
              style={{ color: T.faint }}>
              {monthTicks.map((t) => <span key={t}>{fmtAxis(t)}</span>)}
            </div>
            <div className="relative flex-1 min-w-0">
              {monthTicks.map((t, i) => (
                <span key={t} className="absolute left-0 right-0 border-t"
                  style={{ borderColor: T.lineSoft, top: `${(i / 4) * 100}%` }} />
              ))}
              <div className="relative h-full flex items-end justify-around gap-3">
                {monthlyData.length === 0 ? (
                  <p className="w-full text-center text-[13px] self-center" style={{ color: T.faint }}>No payroll data yet</p>
                ) : monthlyData.map((m, i) => {
                  const amount = m.amount || 0;
                  const isHover = hoverMonth === i;
                  return (
                    <div key={m.name} className="relative flex-1 flex flex-col items-center justify-end h-full max-w-[48px] cursor-default"
                      onMouseEnter={() => setHoverMonth(i)}
                      onMouseLeave={() => setHoverMonth((prev) => (prev === i ? null : prev))}
                    >
                      {isHover && (
                        <div className="absolute bottom-full mb-2 z-10 px-3 py-2 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                          style={{ background: T.ink, border: `1px solid ${T.line}` }}>
                          <p className="text-[11px] font-bold text-white">{m.name}</p>
                          <p className="text-[10.5px] font-medium" style={{ color: '#B9D3EE' }}>
                            ₹{amount.toLocaleString('en-IN')}
                          </p>
                        </div>
                      )}
                      <div className="w-full max-w-[34px] mx-auto rounded-t-[4px] transition-all"
                        style={{
                          height: `${Math.max(amount > 0 ? 4 : 2, (amount / monthTop) * 100)}%`,
                          background: '#3B6FA6',
                          opacity: amount > 0 ? 1 : 0.35,
                          filter: isHover ? 'brightness(1.15)' : 'none',
                          boxShadow: isHover ? '0 0 0 1px rgba(0,0,0,0.08)' : 'none',
                        }} />
                      <span className="text-[10px] font-medium mt-2 transition-colors"
                        style={{ color: isHover ? T.ink : T.muted }}>{m.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Panel>

        <Panel title="Payroll by Department" icon={PieChartIcon}>
          <div className="flex flex-col items-center gap-5">
            <div className="relative w-[132px] h-[132px] flex-shrink-0">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" fill="transparent" stroke={T.lineSoft} strokeWidth="13" />
                {deptData.map((item, i) => {
                  const dash = (item.value / deptTotal) * CIRC;
                  const isHover = hoverDept === i;
                  const seg = (
                    <circle key={i} cx="50" cy="50" r="42" fill="transparent"
                      stroke={DEPT_COLORS[i % DEPT_COLORS.length]} strokeWidth={isHover ? "16" : "13"} strokeLinecap="butt"
                      strokeDasharray={`${dash} ${CIRC}`} strokeDashoffset={`${-deptAcc}`}
                      style={{
                        opacity: hoverDept === null || isHover ? 1 : 0.35,
                        cursor: 'pointer',
                        transition: 'stroke-width 0.15s ease, opacity 0.15s ease',
                      }}
                      onMouseEnter={() => setHoverDept(i)}
                      onMouseLeave={() => setHoverDept((prev) => (prev === i ? null : prev))}
                    />
                  );
                  deptAcc += dash;
                  return seg;
                })}
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-[15px] font-extrabold leading-none text-center px-2" style={{ color: T.ink }}>
                  {hoverDept !== null ? `₹${deptData[hoverDept].value.toLocaleString()}` : `₹${deptRawTotal.toLocaleString()}`}
                </span>
                <span className="text-[9.5px] font-medium mt-1 text-center px-2 leading-tight truncate max-w-full" style={{ color: T.muted }}>
                  {hoverDept !== null ? deptData[hoverDept].name : 'Total'}
                </span>
              </div>
            </div>
            <div className="w-full space-y-2.5">
              {deptData.map((item, i) => (
                <div key={item.name} className="flex items-center justify-between gap-3 cursor-default rounded-md px-1.5 -mx-1.5 py-0.5 transition-colors"
                  style={{ background: hoverDept === i ? T.greenTint : 'transparent' }}
                  onMouseEnter={() => setHoverDept(i)}
                  onMouseLeave={() => setHoverDept((prev) => (prev === i ? null : prev))}
                >
                  <span className="flex items-center min-w-0">
                    <span className="w-2 h-2 rounded-full mr-2 flex-shrink-0" style={{ background: DEPT_COLORS[i % DEPT_COLORS.length] }} />
                    <span className="text-[12.5px] truncate" style={{ color: T.body }}>{item.name}</span>
                  </span>
                  <span className="text-[12.5px] font-semibold whitespace-nowrap" style={{ color: T.ink }}>
                    ₹{item.value.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Panel>
      </div>

      {/* Payroll Details Table */}
      <Panel title="Payroll Details">
        <div className="overflow-x-auto -mx-5">
          <table className="w-full">
            <thead style={{ borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}` }}>
              <tr>
                <th className="px-5 py-3 text-left hrms-label">Employee ID</th>
                <th className="px-5 py-3 text-left hrms-label">Name</th>
                <th className="px-5 py-3 text-left hrms-label">Department</th>
                <th className="px-5 py-3 text-left hrms-label">Designation</th>
                <th className="px-5 py-3 text-left hrms-label">Gross Salary</th>
                <th className="px-5 py-3 text-left hrms-label">Net Salary</th>
                <th className="px-5 py-3 text-left hrms-label">Status</th>
                <th className="px-5 py-3 text-left hrms-label">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: T.lineSoft }}>
              {hasPayroll
                ? payrollRecords.map((p) => (
                    <tr key={p.id} className="transition-colors hover:bg-[#F9F9F6]">
                      <td className="px-5 py-3.5 text-[13px] font-semibold" style={{ color: T.ink }}>{p.employee_id}</td>
                      <td className="px-5 py-3.5 text-[13px] font-semibold" style={{ color: T.ink }}>{p.employee_name}</td>
                      <td className="px-5 py-3.5 text-[13px]" style={{ color: T.body }}>{p.department}</td>
                      <td className="px-5 py-3.5 text-[13px]" style={{ color: T.body }}>{p.position}</td>
                      <td className="px-5 py-3.5 text-[13px]" style={{ color: T.body }}>₹{(Number(p.gross_salary) || 0).toLocaleString()}</td>
                      <td className="px-5 py-3.5 text-[13px]" style={{ color: T.body }}>₹{(Number(p.net_salary) || 0).toLocaleString()}</td>
                      <td className="px-5 py-3.5"><StatusPill status={p.status} /></td>
                      <td className="px-5 py-3.5">
                        <button onClick={() => setViewing({ type: "payroll", data: p })} className="hrms-btn-ghost text-[11.5px] py-1.5 px-3">
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                : employees.map((employee) => (
                    <tr key={employee.id} className="transition-colors hover:bg-[#F9F9F6]">
                      <td className="px-5 py-3.5 text-[13px] font-semibold" style={{ color: T.ink }}>{employee.employee_id}</td>
                      <td className="px-5 py-3.5 text-[13px] font-semibold" style={{ color: T.ink }}>{employee.first_name} {employee.last_name}</td>
                      <td className="px-5 py-3.5 text-[13px]" style={{ color: T.body }}>{employee.department}</td>
                      <td className="px-5 py-3.5 text-[13px]" style={{ color: T.body }}>{employee.position}</td>
                      <td className="px-5 py-3.5 text-[13px]" style={{ color: T.body }}>₹{(Number(employee.salary) || 0).toLocaleString()}</td>
                      <td className="px-5 py-3.5 text-[13px]" style={{ color: T.body }}>₹{((Number(employee.salary) || 0) * 0.9).toLocaleString()}</td>
                      <td className="px-5 py-3.5"><StatusPill status="Not run" /></td>
                      <td className="px-5 py-3.5">
                        <button onClick={() => setViewing({ type: "employee", data: employee })} className="hrms-btn-ghost text-[11.5px] py-1.5 px-3">
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <DetailModal
        open={!!viewing}
        title={
          viewing?.type === "payroll"
            ? viewing.data.employee_name
            : `${viewing?.data?.first_name || ""} ${viewing?.data?.last_name || ""}`.trim() || "Details"
        }
        subtitle={viewing?.type === "payroll" ? `${viewing.data.month} ${viewing.data.year}` : viewing?.data?.position}
        fields={
          viewing?.type === "payroll"
            ? [
                { label: "Employee", value: viewing.data.employee_name },
                { label: "Department", value: viewing.data.department },
                { label: "Position", value: viewing.data.position },
                { label: "Month", value: `${viewing.data.month} ${viewing.data.year}` },
                { label: "Gross Salary", value: `₹${(Number(viewing.data.gross_salary) || 0).toLocaleString()}` },
                { label: "Deductions", value: `₹${(Number(viewing.data.deductions) || 0).toLocaleString()}` },
                { label: "Net Salary", value: `₹${(Number(viewing.data.net_salary) || 0).toLocaleString()}` },
                { label: "Status", value: viewing.data.status },
                { label: "Pay Date", value: viewing.data.pay_date },
              ]
            : viewing
            ? [
                { label: "Employee ID", value: viewing.data.employee_id },
                { label: "Department", value: viewing.data.department },
                { label: "Position", value: viewing.data.position },
                { label: "Gross Salary", value: `₹${(Number(viewing.data.salary) || 0).toLocaleString()}` },
                { label: "Est. Net (−10%)", value: `₹${((Number(viewing.data.salary) || 0) * 0.9).toLocaleString()}` },
                { label: "Status", value: "Payroll not run" },
              ]
            : []
        }
        onClose={() => setViewing(null)}
      />
    </div>
  );
}